import { Order } from "./order";
import { ReturnTypeEnum } from "./return";
import { Team } from "./team";

export const REDO_CUSTOMER_PORTAL_URL =
  process.env.REDO_CUSTOMER_PORTAL_URL || "http://localhost:3002";

export const REDO_REFERRAL_ID_PARAM = "referralId";

export interface CustomerAuth {
  token?: string;
  customer?: { email?: string; name?: string; customerId?: string };
}

/**
 * Get the link to the login page (not logged in)
 * @param team
 * @param userId - Optional userId of the referring agent
 * @param params - Optional query parameters to add to the URL
 * @param useWidgetPath - If true, use the widget path instead of the store domain (useful to avoid CSP restrictions)
 * @returns
 */
export function getCustomerPortalLoginLink(
  team: Team,
  userId?: string,
  params?: Record<string, string>,
  useWidgetPath?: boolean,
): URL {
  const domain = team.portal.domain || team.storeUrl;
  const path = team.portal.pathPrefix || "/apps/redo";

  const url =
    process.env.NODE_ENV === "production" && !useWidgetPath
      ? new URL(`https://${domain}${path}/returns-portal/login`)
      : new URL(
          `${REDO_CUSTOMER_PORTAL_URL}/widget_id/${team.widget_slug}/returns-portal/login`,
        );

  if (userId) {
    url.searchParams.set(REDO_REFERRAL_ID_PARAM, userId);
  }

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
  }

  return url;
}

/**
 * Get a simple, unauthenticated URL to a specific return in the customer portal.
 */
export function getReturnPortalUrl(storeUrl: string, returnId: string): string {
  return `https://${storeUrl}/apps/redo/returns-portal/returns/${returnId}`;
}

export function getBasePortalUrl({
  team,
  auth,
  overrideDomain,
  overridePath,
  useWidgetPath,
}: {
  team: {
    portal: {
      domain?: string | null | undefined;
      pathPrefix?: string | null | undefined;
    };
    storeUrl: string;
    widget_slug: string;
  };
  auth: CustomerAuth;
  overrideDomain?: string;
  overridePath?: string;
  useWidgetPath?: boolean;
}) {
  const domain = overrideDomain || team.portal.domain || team.storeUrl;
  const path = overridePath || team.portal.pathPrefix || "/apps/redo";

  const url =
    process.env.NODE_ENV === "production" && !useWidgetPath
      ? new URL(`https://${domain}${path}/returns-portal`)
      : new URL(
          `${REDO_CUSTOMER_PORTAL_URL}/widget_id/${team.widget_slug}/returns-portal`,
        );
  if (auth.token) {
    url.searchParams.set("token", auth.token);
  }

  return url;
}

export function buildAddressEditLink({
  team,
  auth,
  fulfillmentGroupId,
}: {
  team: Team;
  auth: CustomerAuth;
  fulfillmentGroupId: string;
}) {
  const url = getBasePortalUrl({ team, auth });

  // we dont use the customer object in the portal, but right now the portal requires it to be set in the url.
  // @DannyBoyer said he is planning on refactoring that logic, so I should just send dummy data for now.
  const address = {
    street1: "123 Main St",
    street2: "Apt 1",
    city: "Anytown",
    state: "CA",
    country: "US",
    zip: "12345",
  };

  url.searchParams.set(
    "customer",
    JSON.stringify({ ...auth.customer, address: JSON.stringify(address) }),
  );

  url.searchParams.set("next", `/oms/address-edit/${fulfillmentGroupId}`);
  return url;
}

export function buildPortalUrl({
  team,
  order,
  auth,
  fulfillmentIdx,
  isMerchantCreatedReturn,
  returnType,
  merchantName,
  overrideDomain,
  overridePath,
  useWidgetPath,
}: {
  team: Team;
  order: Order;
  auth: CustomerAuth;
  fulfillmentIdx?: number;
  isMerchantCreatedReturn?: boolean;
  returnType?: ReturnTypeEnum;
  merchantName?: string;
  overrideDomain?: string;
  overridePath?: string;
  useWidgetPath?: boolean;
}): URL {
  const url = getBasePortalUrl({
    team,
    auth,
    overrideDomain,
    overridePath,
    useWidgetPath,
  });

  const address = {
    street1: order.shopify.shipping_address?.address1,
    street2: order.shopify.shipping_address?.address2 || "",
    city: order.shopify.shipping_address?.city,
    state: order.shopify.shipping_address?.province,
    country: order.shopify.shipping_address?.country_code,
    zip: order.shopify.shipping_address?.zip,
  };

  url.searchParams.set(
    "customer",
    JSON.stringify({ ...auth.customer, address: JSON.stringify(address) }),
  );

  let finalDestination = `/returns-portal/orders/${order._id}`;
  if (isMerchantCreatedReturn) {
    finalDestination += `/${returnType}`;
    url.searchParams.set("isMerchantCreatedReturn", "true");
    if (merchantName) {
      url.searchParams.set("merchantName", merchantName);
    }
  } else if (fulfillmentIdx !== undefined) {
    finalDestination += `/track/${fulfillmentIdx}`;
  }
  url.searchParams.set("next", finalDestination);

  return url;
}
